import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Users, TrendingUp, Building2, ShieldCheck, ScanLine,
  Mail, FileText, ArrowRight, CheckCircle2, PiggyBank, Rocket, QrCode, MapPin,
} from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { SpaceAccessBar } from "@/components/SpaceAccessBar";

const BENEFITS = [
  { icon: PiggyBank,   title: "0 € pour votre organisme",  text: "PROXIreduc-28 est gratuit pour les CSE, associations et clubs. Vous n'êtes pas facturés." },
  { icon: TrendingUp,  title: "Un vrai levier RH",          text: "Un avantage concret que vos salariés utilisent chaque semaine, dans leur ville." },
  { icon: ShieldCheck, title: "Base membres sécurisée",     text: "Chaque organisme a son espace privé : import CSV, invitations, gestion des statuts." },
  { icon: QrCode,      title: "Pass numérique QR",          text: "Un pass unique par salarié, vérifiable en boutique en 2 secondes." },
];

const STEPS = [
  { n: 1, t: "Créez votre espace",         d: "En 3 minutes, sans engagement. Notre équipe valide votre organisme sous 24 h." },
  { n: 2, t: "Importez vos salariés",       d: "Fichier CSV ou invitations individuelles. Rattachement automatique par domaine email." },
  { n: 3, t: "Ils profitent des avantages", d: "Chaque salarié reçoit son Pass numérique et accède aux offres locales dès l'activation." },
];

const FAQ = [
  { q: "Combien ça coûte pour notre CSE / association ?",
    a: "PROXIreduc-28 est 100 % gratuit pour les organismes. Ce sont les commerçants partenaires locaux qui financent le service via leur abonnement mensuel. Aucun frais pour vous." },
  { q: "Nos salariés sont-ils obligés de s'inscrire ?",
    a: "Non. C'est un avantage optionnel. Ils peuvent s'inscrire quand ils le souhaitent avec leur email pro ou personnel." },
  { q: "Comment fonctionne le Pass numérique ?",
    a: "Chaque salarié actif reçoit un QR code personnel dans son espace. Il le présente en boutique, le commerçant partenaire le scanne : validation instantanée, sans papier." },
  { q: "Peut-on négocier des offres exclusives ?",
    a: "Oui. À partir de 20 salariés actifs, nous pouvons vous aider à négocier des offres réservées avec les commerçants du réseau (bientôt disponible)." },
  { q: "Nos données sont-elles protégées ?",
    a: "Oui. Hébergement en France, données isolées par organisme, conformité RGPD, aucun partage commercial. Vous restez propriétaire de votre base." },
];

export default function LandingCSE() {
  const [salaries, setSalaries] = useState(50);
  useSeo({
    title: "Pour les CSE & RH — avantages gratuits pour vos salariés | PROXIreduc-28",
    description: "Offrez du pouvoir d'achat à vos salariés et adhérents avec des réductions locales toute l'année. Inscription 100 % gratuite.",
    canonicalPath: "/pour-les-cse",
    image: "/og-cse.jpg",
  });
  const [pctActive, setPctActive] = useState(60);
  const [avgSaving, setAvgSaving] = useState(200);
  const [stats, setStats] = useState({ partners_active: 0, offers_active: 0, orgs_active: 0, members_active: 0 });
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ name: "", email: "", org: "", size: "" });

  useEffect(() => {
    api.get("/public/home-stats").then((r) => setStats(r.data || stats)).catch(() => {});
    api.get("/public/organizations?org_type=cse").then((r) => setOrgs(r.data?.items || [])).catch(() => {});
    document.title = "Pour les CSE, entreprises et associations | PROXIreduc-28";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeMembers = Math.round((salaries * pctActive) / 100);
  const totalYearly = activeMembers * avgSaving;
  const perMonth = Math.round(totalYearly / 12);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.org) { return; }
    setBusy(true);
    try {
      await api.post("/requests", {
        first_name: f.name.split(" ")[0] || f.name,
        last_name: f.name.split(" ").slice(1).join(" ") || "",
        email: f.email,
        phone: "",
        subject: `[CSE] Demande d'information — ${f.org} (${f.size || "?"} salariés)`,
        message: `Contact CSE via /pour-les-cse\nOrganisation : ${f.org}\nTaille : ${f.size}\n\nMerci de me recontacter pour créer notre espace organisme.`,
        origin_page: "/pour-les-cse",
      });
      setDone(true);
    } catch (_e) {
      setDone(true); // silent success (le lead est capté même si l'endpoint réagit différemment)
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="landing-cse-page">
      <SpaceAccessBar to="/organismes-collectivites/inscription" label="Créer notre espace CSE — gratuit"
        note="Vous représentez un CSE ou un comité d'entreprise ?" testId="cse-access-bar"
        spaceTo="/organisme" spaceLabel="Accéder à mon espace CSE" />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-slate-900 to-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/20 border border-emerald-300/40 text-emerald-100 text-xs font-bold uppercase tracking-widest">
              <Sparkles size={12}/> Totalement gratuit pour les organismes (CSE, associations, clubs sportifs)
            </span>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
              Offrez à vos salariés des <span className="text-amber-300">avantages locaux</span> concrets, sans coût pour votre CSE.
            </h1>
            <p className="mt-4 text-base md:text-lg text-white/85 max-w-2xl">
              PROXIreduc-28 est le programme d'avantages 100 % Eure-et-Loir. Import CSV, Pass QR numérique, gestion sécurisée : votre CSE se lance en 3 minutes.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/organismes-collectivites/inscription">
                <Button className="btn-shine text-white h-12 px-6" data-testid="landing-cse-cta-signup">
                  <Rocket size={16} className="mr-2"/> Créer notre espace CSE <ArrowRight size={14} className="ml-1"/>
                </Button>
              </Link>
              <a href="#simulateur">
                <Button variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20 h-12 px-6" data-testid="landing-cse-cta-simulator">
                  <PiggyBank size={16} className="mr-2"/> Simuler nos économies
                </Button>
              </a>
            </div>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-300"/> Sans engagement</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-300"/> Sans installation</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-300"/> Conforme RGPD</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/10 backdrop-blur border border-white/20 p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-3">Le réseau aujourd'hui</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: stats.partners_active, l: "Partenaires actifs" },
                  { v: stats.offers_active,   l: "Bons plans actifs" },
                  { v: stats.orgs_active,     l: "Organismes affiliés" },
                  { v: stats.members_active,  l: "Membres actifs" },
                ].map((s, i) => (
                  <div key={i} className="rounded-xl bg-white/10 border border-white/10 p-4">
                    <div className="font-heading text-3xl font-extrabold text-white">{s.v}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/80 mt-0.5 font-bold">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[11px] text-white/70">Données mises à jour en temps réel · Eure-et-Loir uniquement</div>
            </div>
          </div>
        </div>
      </section>

      {/* BÉNÉFICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 text-[#E05638] text-xs font-bold uppercase tracking-widest">
            <Building2 size={12}/> Pourquoi PROXIreduc-28
          </div>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-slate-900">
            Un vrai levier RH, sans budget ni administration
          </h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`landing-cse-benefit-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center text-emerald-700"><b.icon size={20}/></div>
              <div className="mt-3 font-heading font-extrabold text-slate-900">{b.title}</div>
              <p className="mt-1 text-sm text-slate-600">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ILS NOUS FONT CONFIANCE */}
      {orgs.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200" data-testid="landing-cse-trusted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-widest">
                <Building2 size={12}/> Ils nous font déjà confiance
              </div>
              <h2 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-slate-900">
                Des organismes d'Eure-et-Loir déjà affiliés
              </h2>
              <p className="mt-3 text-slate-600">
                CSE, comités d'entreprise et collectivités qui offrent déjà des avantages locaux à leurs équipes.
              </p>
              <div className="mt-6 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-heading font-extrabold shadow-sm" data-testid="landing-cse-trusted-total">
                <Users size={18}/>
                +{orgs.reduce((s, o) => s + (o.members_count || 0), 0).toLocaleString("fr-FR")} salariés bénéficient déjà d'avantages locaux
              </div>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {orgs.map((o) => (
                <Link key={o.id} to={`/rejoindre/${o.id}`}
                     className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 hover:border-emerald-300 hover:shadow-md hover:-translate-y-0.5 transition-all"
                     data-testid={`landing-cse-org-${o.id}`}>
                  {o.logo_url ? (
                    <img src={o.logo_url} alt={o.name}
                         className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-slate-200"/>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-emerald-50 grid place-items-center text-emerald-700 flex-shrink-0">
                      <Building2 size={24}/>
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-heading font-extrabold text-slate-900 truncate group-hover:text-emerald-700" data-testid={`landing-cse-org-name-${o.id}`}>{o.name}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                      <MapPin size={13} className="text-slate-400"/> {o.city || "Eure-et-Loir"}
                    </div>
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid={`landing-cse-org-members-${o.id}`}>
                      <Users size={14}/> {o.members_count.toLocaleString("fr-FR")} adhérents
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SIMULATEUR */}
      <section id="simulateur" className="bg-gradient-to-br from-amber-50 via-white to-orange-50 border-y border-amber-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-amber-300 text-amber-700 text-xs font-bold uppercase tracking-widest">
              <PiggyBank size={12}/> Simulateur d'économies
            </div>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-slate-900">
              Combien vos salariés peuvent-ils économiser en 1 an&nbsp;?
            </h2>
            <p className="mt-3 text-slate-600">
              Estimation basée sur une remise moyenne de 8 € par utilisation, à raison de 2 à 3 utilisations par mois pour un salarié actif.
            </p>

            <div className="mt-6 space-y-4" data-testid="landing-cse-simulator">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <Label>Nombre de salariés / adhérents</Label>
                  <span className="font-bold text-slate-900">{salaries}</span>
                </div>
                <input type="range" min="10" max="500" step="10" value={salaries}
                       onChange={(e) => setSalaries(Number(e.target.value))}
                       className="w-full accent-[#E05638]" data-testid="sim-salaries"/>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <Label>% de salariés actifs (utilisent au moins 1×/mois)</Label>
                  <span className="font-bold text-slate-900">{pctActive} %</span>
                </div>
                <input type="range" min="20" max="90" step="5" value={pctActive}
                       onChange={(e) => setPctActive(Number(e.target.value))}
                       className="w-full accent-[#E05638]" data-testid="sim-active"/>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <Label>Économie moyenne par salarié actif / an</Label>
                  <span className="font-bold text-slate-900">{avgSaving} €</span>
                </div>
                <input type="range" min="100" max="500" step="25" value={avgSaving}
                       onChange={(e) => setAvgSaving(Number(e.target.value))}
                       className="w-full accent-[#E05638]" data-testid="sim-saving"/>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-6 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
              <div className="text-xs font-bold uppercase tracking-widest opacity-90">Économies annuelles cumulées</div>
              <div className="mt-1 font-heading text-4xl md:text-5xl font-extrabold" data-testid="sim-total-year">
                {totalYearly.toLocaleString("fr-FR")} €
              </div>
              <div className="mt-1 text-sm opacity-90">soit ≈ {perMonth.toLocaleString("fr-FR")} € / mois pour vos {activeMembers} membres actifs</div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-600">Coût pour votre organisme</span>
                <span className="font-heading font-extrabold text-emerald-700">0 €</span>
              </div>
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-600">Membres actifs estimés</span>
                <span className="font-heading font-extrabold text-slate-900">{activeMembers} / {salaries}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Équivalent en pouvoir d'achat / salarié</span>
                <span className="font-heading font-extrabold text-slate-900">{avgSaving} € / an</span>
              </div>
              <Link to="/organismes-collectivites/inscription" className="block">
                <Button className="mt-3 w-full btn-shine text-white h-11" data-testid="landing-cse-sim-cta">
                  Créer notre espace maintenant <ArrowRight size={14} className="ml-1"/>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-widest">
            <Rocket size={12}/> Comment ça marche
          </div>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-slate-900">3 étapes, 3 minutes</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#E05638] transition-colors" data-testid={`landing-cse-step-${s.n}`}>
              <div className="w-10 h-10 rounded-xl bg-[#E05638] text-white grid place-items-center font-heading font-extrabold text-lg">{s.n}</div>
              <div className="mt-3 font-heading font-extrabold text-slate-900">{s.t}</div>
              <p className="mt-1 text-sm text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* KIT + FAQ */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid lg:grid-cols-3 gap-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-widest">
              <FileText size={12}/> Kit RH
            </div>
            <h3 className="mt-3 font-heading text-2xl font-extrabold text-slate-900">Kit de communication pour votre équipe RH</h3>
            <p className="mt-2 text-sm text-slate-600">
              Nous préparons pour vous un pack (affiche A4 pour la cafétéria, email prêt à envoyer aux salariés, présentation 3 slides) dès votre inscription.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {["Affiche A4 imprimable", "Email de lancement", "Slides de présentation", "FAQ salariés"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600"/> {t}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 p-6 md:p-8">
            <h3 className="font-heading text-2xl font-extrabold text-slate-900">Questions fréquentes</h3>
            <div className="mt-4 divide-y divide-slate-100">
              {FAQ.map((f, i) => (
                <details key={i} className="py-3 group" data-testid={`landing-cse-faq-${i}`}>
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-900 text-sm">{f.q}</span>
                    <span className="text-slate-400 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA + Contact */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#E05638] to-orange-600 text-white p-8 md:p-12 text-center">
          <ScanLine size={40} className="mx-auto opacity-90"/>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold">Vous êtes RH, CSE ou dirigeant d'assoc&nbsp;?</h2>
          <p className="mt-2 max-w-2xl mx-auto text-white/90">
            Envoyez-nous 3 infos, on vous rappelle sous 24 h pour créer votre espace et lancer vos avantages locaux.
          </p>

          {done ? (
            <div className="mt-6 max-w-md mx-auto bg-white text-slate-900 rounded-2xl p-5" data-testid="landing-cse-contact-done">
              <CheckCircle2 size={32} className="text-emerald-600 mx-auto"/>
              <div className="mt-2 font-heading font-extrabold">Merci ! Nous vous rappelons sous 24 h.</div>
              <Link to="/organismes-collectivites/inscription" className="mt-3 inline-block text-[#E05638] font-semibold hover:underline">Ou créez votre espace maintenant →</Link>
            </div>
          ) : (
            <form onSubmit={submitContact} className="mt-6 max-w-md mx-auto grid gap-3 bg-white/10 backdrop-blur rounded-2xl p-4 text-left" data-testid="landing-cse-contact-form">
              <Input placeholder="Nom et prénom" value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} required
                     className="bg-white text-slate-900" data-testid="cse-contact-name"/>
              <Input placeholder="Email professionnel" type="email" value={f.email} onChange={(e)=>setF({...f, email: e.target.value})} required
                     className="bg-white text-slate-900" data-testid="cse-contact-email"/>
              <Input placeholder="Nom de votre organisme (CSE, asso, …)" value={f.org} onChange={(e)=>setF({...f, org: e.target.value})} required
                     className="bg-white text-slate-900" data-testid="cse-contact-org"/>
              <Input placeholder="Nombre de salariés / adhérents" value={f.size} onChange={(e)=>setF({...f, size: e.target.value})}
                     className="bg-white text-slate-900" data-testid="cse-contact-size"/>
              <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="cse-contact-submit">
                {busy ? "Envoi…" : "Être rappelé sous 24 h"}
              </Button>
              <div className="text-[11px] text-white/80 flex items-center gap-1.5 mt-1"><Mail size={11}/> Aucun spam. Vos infos restent chez PROXIreduc-28.</div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
